import { useEffect, useRef } from "react";
import { CandlestickSeries, createChart, HistogramSeries } from "lightweight-charts";
import { RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

export const CandleCanvas = ({ candles, prefix, onInspect }) => {
  const container = useRef(null);
  const chartRef = useRef(null);
  const prices = useRef(null);
  const volumes = useRef(null);
  const fitted = useRef(false);

  useEffect(() => {
    const element = container.current;
    const chart = createChart(element, {
      autoSize: true,
      localization: { locale: "en-US" },
      layout: { background: { type: "solid", color: "#050e0a" }, textColor: "#a6cbb7", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, attributionLogo: true },
      grid: { vertLines: { color: "#153024" }, horzLines: { color: "#153024" } },
      rightPriceScale: { borderColor: "#2a4938", scaleMargins: { top: 0.12, bottom: 0.26 } },
      timeScale: { borderColor: "#2a4938", timeVisible: true, secondsVisible: false, rightOffset: 4, barSpacing: 10 },
      crosshair: { vertLine: { color: "#779d89" }, horzLine: { color: "#779d89" } },
      handleScroll: { mouseWheel: false, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
      handleScale: { mouseWheel: false, pinch: true, axisPressedMouseMove: true },
    });
    const series = chart.addSeries(CandlestickSeries, { upColor: "#47f19a", downColor: "#ff807b", borderVisible: false, wickUpColor: "#47f19a", wickDownColor: "#ff807b" });
    const volume = chart.addSeries(HistogramSeries, { priceFormat: { type: "volume" }, priceScaleId: "volume", lastValueVisible: false, priceLineVisible: false });
    volume.priceScale().applyOptions({ scaleMargins: { top: 0.83, bottom: 0 }, visible: false });
    chart.subscribeCrosshairMove((event) => {
      const candle = event.seriesData.get(series);
      const histogram = event.seriesData.get(volume);
      onInspect(candle?.close !== undefined ? { ...candle, volume: histogram?.value } : null);
    });
    element.querySelectorAll("a").forEach((link, index) => { link.dataset.testid = `${prefix}-chart-library-link-${index}`; });
    element.querySelectorAll("canvas").forEach((canvas, index) => { canvas.dataset.testid = `${prefix}-chart-canvas-${index}`; });
    chartRef.current = chart;
    prices.current = series;
    volumes.current = volume;
    fitted.current = false;
    return () => { chart.remove(); chartRef.current = null; prices.current = null; volumes.current = null; };
  }, [prefix, onInspect]);

  useEffect(() => {
    if (!prices.current || !candles.length) return;
    const precision = Math.min(12, Math.max(2, 3 - Math.floor(Math.log10(candles[candles.length - 1].close))));
    prices.current.applyOptions({ priceFormat: { type: "price", precision, minMove: 10 ** -precision } });
    prices.current.setData(candles.map(({ volume, ...candle }) => candle));
    volumes.current.setData(candles.map(({ time, open, close, volume }) => ({ time, value: volume, color: close >= open ? "#237448" : "#80403c" })));
    if (!fitted.current) { chartRef.current.timeScale().fitContent(); fitted.current = true; }
  }, [candles]);

  return <div className="market-canvas-wrap" data-testid={`${prefix}-chart-plot`}>
    <div ref={container} className="market-canvas" data-testid={`${prefix}-chart-canvas`} role="img" aria-label="Real token candlesticks and trading volume in US dollars. Times are UTC." />
    <Button variant="outline" size="icon" className="market-reset" title="Reset chart view" aria-label="Reset chart view" data-testid={`${prefix}-chart-reset`} onClick={() => chartRef.current?.timeScale().fitContent()}><RotateCcw aria-hidden="true" /></Button>
  </div>;
};